import React, { useRef, useEffect, useState } from 'react';
import { View, StyleSheet, Platform, Pressable, ImageBackground, TouchableOpacity, Animated, Text, Image } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Typography, Button } from './ui';
import { useTheme } from '../context/ThemeContext';
import { LinearGradient } from 'expo-linear-gradient';
import { MasterEvent, EventType } from '../types/auth';
import { getFileUrl } from '../services/api';
import { useI18n } from '../i18n';
import { ShareModal } from './ShareModal';
import { favoriteIconName, favoriteA11yLabel } from './favoriteIcon';
import { buildShareText, shareOrOpenModal } from '../utils/roomShare';
import type { ShareTarget } from '../services/shareAnalytics';
import { fontWeights } from '../styles/typographyTokens';

import { inkOnFill, ON_ACCENT_TEXT } from '../styles/accentText';
import { surfaceFill } from '../styles/platformBackgrounds';
/** CORE-1995 — обложка события без своей картинки: файл из бандла (настоящий JPEG). */
const DEFAULT_EVENT_COVER = require('../../assets/events/community_gathering.jpg');

const EVENT_TYPE_COLORS: Record<string, string> = {
    'retreat': '#2D8A4E',
    'workshop': '#6B5CE7',
    'ceremony': '#E98245',
    'class': '#2196F3',
    'festival': '#E91E8C',
    'practice': '#38A169',
};

interface MasterEventModalProps {
    event: MasterEvent | null;
    masterName: string;
    masterPhotoUrl: string | null;
    onClose: () => void;
    onAddToCalendar: (event: MasterEvent) => void;
    onFavorite: (event: MasterEvent) => void;
    isAuthenticated: boolean;
    isSaved?: boolean;
}

export const MasterEventModal: React.FC<MasterEventModalProps> = ({ 
    event, 
    masterName, 
    masterPhotoUrl, 
    onClose, 
    onAddToCalendar, 
    onFavorite, 
    isAuthenticated,
    isSaved = false
}) => {
    const { isDark, colors: themeColors } = useTheme();
    const { i18n } = useI18n('common');
    const [showShareModal, setShowShareModal] = useState(false);
    const slideAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        if (event) {
            Animated.spring(slideAnim, {
                toValue: 1,
                useNativeDriver: true,
                speed: 16,
                bounciness: 4,
            }).start();
        } else {
            slideAnim.setValue(0);
        }
    }, [event, slideAnim]);

    if (!event) return null;

    const startDate = new Date(event.startDateUtc);
    const day = startDate.getDate();
    const month = startDate.toLocaleDateString('en', { month: 'short' }).toUpperCase();
    const time = startDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    
    // Duration
    let durationStr = '';
    if (event.endDateUtc) {
        const endDate = new Date(event.endDateUtc);
        const mins = Math.round((endDate.getTime() - startDate.getTime()) / 60000);
        if (mins < 60) durationStr = `${mins}m`;
        else if (mins < 24 * 60) durationStr = `${Math.round(mins / 60)}h`;
        else durationStr = `${Math.round(mins / (24 * 60))} days`;
    }

    const accentColor = EVENT_TYPE_COLORS[event.eventType] || '#E98245';

    // CORE-978 — one share text for the event, built from i18n, pointing at the
    // real deep link (`/lives?eventId=`, see utils/share). A phone gets the OS
    // sheet, a desktop the branded ShareModal (CORE-786). The old
    // code hand-built `/events/${id}` — a route that never existed, so the link
    // resolved to the master's profile (CORE-630) — and a hard-coded domain.
    const share = buildShareText({ type: 'event', id: event.id, title: event.title });
    const shareTarget: ShareTarget = { entityType: 'event', entityId: event.id, surface: 'event_card' };

    const panelTranslateY = slideAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [50, 0]
    });
    const panelOpacity = slideAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 1]
    });

    // CORE-1995 — без обложки показываем свою картинку из бандла, а не снимок
    // с хоста Unsplash: из РФ он не открывается.
    const coverUrl = event.coverImageUrl ? getFileUrl(event.coverImageUrl) : null;
    const coverSource = coverUrl ? { uri: coverUrl } : DEFAULT_EVENT_COVER;
    const avatarUrl = masterPhotoUrl ? getFileUrl(masterPhotoUrl) : null;

    return (
        <>
        <Pressable style={s.backdrop} onPress={onClose}>
            <Animated.View
                style={[
                    s.modal,
                    // CORE-1733 (возврат QA) — окно события — поверхность v4; внутренний
                    // блок ниже обложки прозрачный: непрозрачные поверхности не вкладываются.
                    surfaceFill(isDark ? 'dark' : 'light'),
                    {
                        opacity: panelOpacity,
                        transform: [{ translateY: panelTranslateY }]
                    },
                    Platform.OS === 'web' && {
                        boxShadow: isDark
                            ? '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)'
                            : '0 24px 80px rgba(0,0,0,0.15)',
                    } as any
                ]}
            >
                <Pressable onPress={(e) => e.stopPropagation()} style={{ width: '100%' }}>
                    <TouchableOpacity style={s.closeBtn} onPress={onClose} activeOpacity={0.7}>
                        <Ionicons name="close" size={20} color="#FFF" />
                    </TouchableOpacity>

                    <ImageBackground
                        source={coverSource}
                        style={s.headerBg}
                    >
                        <LinearGradient
                            colors={['transparent', 'rgba(0,0,0,0.85)']}
                            locations={[0.1, 1]}
                            style={s.headerOverlay}
                        />
                        <View style={s.headerContent}>
                            <View style={s.headerTop}>
                                <View style={s.badgeRow}>
                                    <View style={[s.catBadge, { backgroundColor: accentColor }]}>
                                        <Text style={[s.badgeText, { color: inkOnFill(accentColor) }]}>
                                            {String(event.eventType).toUpperCase()}
                                        </Text>
                                    </View>
                                    {event.isOnline ? (
                                        <View style={[s.statusBadge, { backgroundColor: 'rgba(56,161,105,0.9)' }]}>
                                            <Ionicons name="videocam" size={11} color="#FFF" style={{ marginRight: 4 }} />
                                            <Text style={s.badgeText}>ONLINE</Text>
                                        </View>
                                    ) : (
                                        <View style={[s.statusBadge, { backgroundColor: 'rgba(232,93,4,0.9)' }]}>
                                            <Ionicons name="location" size={11} color={ON_ACCENT_TEXT} style={{ marginRight: 4 }} />
                                            <Text style={[s.badgeText, { color: ON_ACCENT_TEXT }]}>OFFLINE</Text>
                                        </View>
                                    )}
                                </View>
                            </View>
                            
                            <Typography variant="h3" style={s.title}>{event.title}</Typography>
                            {event.subtitle && (
                                <Typography variant="body" style={s.subtitle}>{event.subtitle}</Typography>
                            )}

                            <View style={s.hostRow}>
                                {avatarUrl ? (
                                    <Image source={{ uri: avatarUrl }} style={s.hostAvatar} />
                                ) : (
                                    <View style={[s.hostAvatar, { backgroundColor: accentColor, alignItems: 'center', justifyContent: 'center' }]}>
                                        <Ionicons name="person" size={12} color={inkOnFill(accentColor)} />
                                    </View>
                                )}
                                <Typography variant="caption" style={s.hostedByText}>
                                    Hosted by <Text style={s.hostNameText}>{masterName}</Text>
                                </Typography>
                            </View>
                        </View>
                    </ImageBackground>

                    <View style={[s.content, { backgroundColor: 'transparent' }]}>
                        <View style={s.infoGrid}>
                            <View style={[s.infoBox, { backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#F9F9F8' }]}>
                                <View style={[s.infoIconWrap, { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }]}>
                                    <Ionicons name="calendar-outline" size={18} color={accentColor} />
                                </View>
                                <View>
                                    <Typography variant="h5" style={{ color: themeColors.textPrimary, fontSize: 16, fontWeight: fontWeights.bold }}>{month} {day}</Typography>
                                    <Typography variant="caption" style={{ color: themeColors.textSecondary, fontSize: 13 }}>{time} {durationStr ? `· ${durationStr}` : ''}</Typography>
                                </View>
                            </View>
                            <View style={[s.infoBox, { backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#F9F9F8' }]}>
                                <View style={[s.infoIconWrap, { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }]}>
                                    <Ionicons name={event.isOnline ? "globe-outline" : "location-outline"} size={18} color={accentColor} />
                                </View>
                                <View style={{ flex: 1 }}>
                                    <Typography variant="h5" style={{ color: themeColors.textPrimary, fontSize: 16, fontWeight: fontWeights.bold }}>{event.isOnline ? 'Virtual' : 'In-Person'}</Typography>
                                    {event.location && (
                                        <Typography variant="caption" style={{ color: themeColors.textSecondary, fontSize: 13 }} numberOfLines={1}>{event.location}</Typography>
                                    )}
                                </View>
                            </View>
                        </View>

                        {event.description && (
                            <>
                                <Typography variant="overline" style={[s.sectionLabel, { color: themeColors.textSecondary }]}>About Event</Typography>
                                <Typography variant="body" style={{ color: themeColors.textPrimary, fontSize: 15, lineHeight: 24, marginBottom: 28 }}>
                                    {event.description}
                                </Typography>
                            </>
                        )}

                        <View style={[s.footer, { borderTopColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }]}>
                            <TouchableOpacity
                                onPress={() => onFavorite(event)}
                                accessibilityRole="button"
                                accessibilityLabel={favoriteA11yLabel(isSaved)}
                                style={[s.actionBtnOutline, { borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' }]}
                            >
                                <Ionicons name={favoriteIconName(isSaved)} size={20} color={isSaved ? accentColor : themeColors.textPrimary} />
                            </TouchableOpacity>

                            <TouchableOpacity
                                onPress={() => {
                                    // CORE-786 — a phone gets the OS sheet, a desktop the popup.
                                    void shareOrOpenModal(
                                        { url: share.url, title: event.title, subject: share.subject, message: share.message },
                                        shareTarget,
                                        () => setShowShareModal(true),
                                    );
                                }}
                                style={[s.actionBtnOutline, { borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)', marginLeft: 8 }]}
                            >
                                <Ionicons name="share-outline" size={20} color={themeColors.textPrimary} />
                            </TouchableOpacity>

                            <View style={{ flex: 1, alignItems: 'flex-end' }}>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    style={[s.joinBtn, { backgroundColor: accentColor, borderColor: accentColor }]}
                                    onPress={() => onAddToCalendar(event)}
                                >
                                    <Ionicons name="calendar-outline" size={16} color={inkOnFill(accentColor)} style={{ marginRight: 6 }} />
                                    Add to Calendar
                                </Button>
                            </View>
                        </View>
                    </View>
                </Pressable>
            </Animated.View>
        </Pressable>

        <ShareModal
            visible={showShareModal}
            onClose={() => setShowShareModal(false)}
            url={share.url}
            title={event.title}
            subject={share.subject}
            message={share.message}
            language={i18n.language}
            analytics={shareTarget}
        />
        </>
    );
};

const s = StyleSheet.create({
    backdrop: {
        position: Platform.OS === 'web' ? 'fixed' : 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.55)',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        padding: 20,
        ...(Platform.OS === 'web' && { backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' } as any)
    } as any,
    modal: {
        width: '100%',
        maxWidth: 520,
        borderRadius: 24,
        overflow: 'hidden',
    },
    closeBtn: {
        position: 'absolute',
        top: 20, right: 20,
        width: 36, height: 36,
        borderRadius: 18,
        backgroundColor: 'rgba(0,0,0,0.4)',
        alignItems: 'center', justifyContent: 'center',
        zIndex: 10,
        ...(Platform.OS === 'web' && { backdropFilter: 'blur(12px)' } as any)
    },
    headerBg: {
        width: '100%',
        height: 260,
        justifyContent: 'flex-end',
    },
    headerOverlay: {
        ...StyleSheet.absoluteFillObject,
    } as any,
    headerContent: {
        paddingHorizontal: 28,
        paddingBottom: 28,
        paddingTop: 12,
        position: 'relative',
        zIndex: 2,
    },
    headerTop: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 16,
    },
    badgeRow: {
        flexDirection: 'row',
        gap: 8,
    },
    catBadge: {
        paddingHorizontal: 12, paddingVertical: 5,
        borderRadius: 100,
    },
    statusBadge: {
        flexDirection: 'row', alignItems: 'center',
        paddingHorizontal: 10, paddingVertical: 5,
        borderRadius: 100,
    },
    badgeText: {
        color: '#FFF',
        fontWeight: fontWeights.bold,
        fontSize: 13,
        letterSpacing: 1,
        ...(Platform.OS === 'web' && { fontFamily: 'var(--font-main)' } as any),
    } as any,
    title: {
        color: '#FFF',
        fontSize: 28,
        fontWeight: fontWeights.bold,
        letterSpacing: -0.5,
        marginBottom: 6,
        lineHeight: 34,
    } as any,
    subtitle: {
        color: 'rgba(255,255,255,0.85)',
        fontSize: 16,
        fontWeight: '500',
        marginBottom: 16,
    } as any,
    hostRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    hostAvatar: {
        width: 32,
        height: 32,
        borderRadius: 16,
        borderWidth: 2,
        borderColor: 'rgba(255,255,255,0.5)',
    },
    hostedByText: {
        color: 'rgba(255,255,255,0.7)',
        fontWeight: '500',
        fontSize: 14,
    } as any,
    hostNameText: {
        color: '#FFF',
        fontWeight: fontWeights.bold,
    },
    content: {
        padding: 28,
    },
    sectionLabel: {
        fontSize: 13,
        letterSpacing: 1.5,
        fontWeight: fontWeights.bold,
        marginBottom: 12,
        textTransform: 'uppercase',
    } as any,
    infoGrid: {
        flexDirection: 'row',
        gap: 12,
        marginBottom: 28,
    },
    infoBox: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        paddingVertical: 16,
        paddingHorizontal: 16,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: 'rgba(0,0,0,0.02)',
    },
    infoIconWrap: {
        width: 40, height: 40,
        borderRadius: 20,
        alignItems: 'center', justifyContent: 'center',
    },
    footer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 4,
        paddingTop: 24,
        borderTopWidth: StyleSheet.hairlineWidth,
        gap: 12,
    },
    actionBtnOutline: {
        width: 54,
        height: 54,
        borderRadius: 27,
        borderWidth: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    joinBtn: {
        height: 44,
        paddingHorizontal: 20,
        borderRadius: 22,
        flexDirection: 'row',
    },
});
